import * as THREE from 'three';
import { getLookPreset } from '../../settings/render';

/**
 * Maps legacy Phong shininess (1 / 10 / 50) to PBR roughness.
 */
export function shininessToRoughness( shininess = 10 ) {

  const s = Math.max( 0, Number( shininess ) || 10 );

  if ( s <= 2 ) return 0.62;
  if ( s >= 40 ) return 0.34;

  return 0.5;

}

function configureMap( texture, size, rotation, { sRGB = true } = {} ) {

  if ( !texture ) return texture;

  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.encoding = sRGB ? THREE.sRGBEncoding : THREE.LinearEncoding;
  texture.anisotropy = 8;

  if ( size?.[ 2 ] ) {

    texture.repeat.set( size[ 0 ], size[ 1 ] );

  }

  if ( rotation ) {

    texture.rotation = Math.PI / 2;

  }

  texture.needsUpdate = true;

  return texture;

}

function profileForTarget( target = '', shininess = 10 ) {

  // sill = подоконник; windowSill / windows = оконные рамы (PVC)
  const isSill = target === 'sill';
  const isFrame = target === 'windowSill' || target === 'window' || target === 'windows';
  const isFloor = target === 'floor';
  const isCeiling = target === 'ceiling';
  const isWall = /Wall|wall/.test( target ) || [ 'longWall', 'homeLongWall', 'shortWall', 'smallShortWall' ].includes( target );

  // Default wall / lining
  let roughness = shininessToRoughness( shininess );
  let metalness = 0;
  let clearcoat = 0.08;
  let clearcoatRoughness = 0.45;
  let normalScale = 1.35;
  let envBoost = 0;
  let usePhysical = true;

  if ( isFloor ) {

    roughness = 0.42;
    clearcoat = 0.35;
    clearcoatRoughness = 0.28;
    normalScale = 1.15;
    envBoost = 0.15;

  } else if ( isSill ) {

    // Glossy plastic window ledge
    roughness = 0.28;
    metalness = 0.06;
    clearcoat = 0.55;
    clearcoatRoughness = 0.2;
    normalScale = 0.55;
    envBoost = 0.25;

  } else if ( isFrame ) {

    // PVC / painted frame — softer sheen than sill, slight metalness for edge catch
    roughness = 0.38;
    metalness = 0.12;
    clearcoat = 0.28;
    clearcoatRoughness = 0.32;
    normalScale = 0.35;
    envBoost = 0.22;

  } else if ( isCeiling ) {

    roughness = 0.55;
    clearcoat = 0.05;
    clearcoatRoughness = 0.6;
    normalScale = 0.5;
    envBoost = -0.05;

  } else if ( isWall ) {

    // Wood lining / bricks — readable microrelief
    roughness = shininess <= 2 ? 0.58 : 0.48;
    clearcoat = shininess <= 2 ? 0.12 : 0.06;
    clearcoatRoughness = 0.4;
    normalScale = shininess <= 2 ? 1.55 : 1.9;
    envBoost = 0.05;

  } else if ( String( target ).indexOf( 'cupboard' ) !== -1 || String( target ).indexOf( 'lar' ) !== -1 ) {

    // Body: varnish wood — clearcoat + mid roughness (handles/tops get variants at apply-time)
    roughness = 0.38;
    clearcoat = 0.32;
    clearcoatRoughness = 0.28;
    normalScale = 0.85;
    envBoost = 0.14;

  }

  return {

    roughness,
    metalness,
    clearcoat,
    clearcoatRoughness,
    normalScale,
    envBoost,
    usePhysical,

  };

}

/**
 * Builds MeshPhysical / MeshStandard material for balcony parts.
 */
export function createPbrMaterial({

  map,
  normalMap = null,
  roughnessMap = null,
  aoMap = null,
  size,
  rotation = false,
  shininess = 10,
  target = '',
  stretchCeiling = false,
  envMapIntensity,

}) {

  const look = getLookPreset();
  const baseIntensity = envMapIntensity ?? look.envMapIntensity;
  const configuredMap = configureMap( map, size, rotation, { sRGB: true } );
  const configuredNormal = configureMap( normalMap, size, rotation, { sRGB: false } );
  const configuredRoughness = configureMap( roughnessMap, size, rotation, { sRGB: false } );
  const configuredAo = configureMap( aoMap, size, rotation, { sRGB: false } );

  const isStretch = stretchCeiling || ( target === 'ceiling' && configuredMap?.image?.src?.includes( 'white.jpg' ) );

  if ( isStretch ) {

    const physical = new THREE.MeshPhysicalMaterial({

      map: configuredMap,
      color: 0xffffff,
      roughness: 0.12,
      metalness: 0,
      clearcoat: 0.9,
      clearcoatRoughness: 0.12,
      envMapIntensity: Math.min( 1.25, baseIntensity + 0.4 ),
      side: THREE.FrontSide,

    });

    physical.userData.isConstructorMaterial = true;

    return physical;

  }

  const profile = profileForTarget( target, shininess );
  let roughness = profile.roughness;

  // roughnessMap multiplies — keep base below 1 so generated maps stay readable
  if ( configuredRoughness ) {

    roughness = Math.min( 0.92, roughness + 0.15 );

  }

  const intensity = Math.max( 0.15, baseIntensity + profile.envBoost );

  const mat = new THREE.MeshPhysicalMaterial({

    map: configuredMap,
    roughness,
    metalness: profile.metalness,
    clearcoat: profile.clearcoat,
    clearcoatRoughness: profile.clearcoatRoughness,
    envMapIntensity: intensity,
    side: THREE.FrontSide,
    reflectivity: 0.5,

  });

  if ( configuredNormal ) {

    mat.normalMap = configuredNormal;
    mat.normalScale = new THREE.Vector2( profile.normalScale, profile.normalScale );

  }

  if ( configuredRoughness ) {

    mat.roughnessMap = configuredRoughness;

  }

  if ( configuredAo ) {

    mat.aoMap = configuredAo;
    mat.aoMapIntensity = 0.9;

  }

  mat.userData.isConstructorMaterial = true;

  return mat;

}

/** Dispose previous material + its maps when swapping textures */
export function disposeMaterial( material ) {

  if ( !material ) return;

  // Furniture role clones share map refs with the body material — only dispose the material itself
  if ( !material.userData?.sharedMaps ) {

    const maps = [

      'map',
      'normalMap',
      'roughnessMap',
      'metalnessMap',
      'aoMap',
      'emissiveMap',

    ];

    maps.forEach(( key ) => {

      if ( material[ key ] ) material[ key ].dispose();

    });

  }

  material.dispose();

}
